/** Group consult webinar session returned by the scheduling API. */
export type GroupConsultEvent = {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  registerUrl?: string;
  /** From Zoom registrant cap: few seats left for this session. */
  limitedAvailability?: boolean;
};

/** Sessions grouped under a single calendar day in the selected display timezone. */
export type GroupConsultDayBucket = {
  dayKey: string;
  date: Date;
  events: GroupConsultEvent[];
  titles: string[];
};

/** True for a string `new Date()` can turn into a real instant. */
function isParseableInstant(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(new Date(value).getTime());
}

/**
 * Narrow one element of the `GET /consult-scheduling/webinars` response to a session the render
 * path can actually handle.
 *
 * @remarks The fields checked are exactly the ones whose absence or wrong shape is an OUTAGE rather
 * than a cosmetic gap. `start` and `end` are fed to `Intl.DateTimeFormat`, whose `format` and
 * `formatToParts` both throw `RangeError: Invalid time value` on an Invalid Date — during render,
 * with no error boundary above them until this PR added one. `id` is the React key and the
 * registration path parameter, and `title` is `.trim()`ed while building day buckets.
 *
 * `description`, `registerUrl` and `limitedAvailability` are deliberately NOT checked: each is
 * optional, each degrades to absent copy, and none reaches a throwing call.
 */
export function isRenderableGroupConsultEvent(value: unknown): value is GroupConsultEvent {
  if (typeof value !== 'object' || value === null) return false;
  if (!('id' in value) || typeof value.id !== 'string' || value.id.trim() === '') return false;
  if (!('title' in value) || typeof value.title !== 'string') return false;
  return (
    'start' in value &&
    isParseableInstant(value.start) &&
    'end' in value &&
    isParseableInstant(value.end)
  );
}

/** Format ISO start/end in the given timezone for display. */
export function formatEventTimeRange(startIso: string, endIso: string, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const start = new Date(startIso);
  const end = new Date(endIso);
  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

/**
 * Format a same-day consult range compactly while still falling back to the full range when needed.
 */
export function formatEventTimeRangeCompact(
  startIso: string,
  endIso: string,
  timeZone: string,
): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  // The vendor detached `.format` off the formatter; kept on the instance here so the lint rule
  // against unbound methods stays satisfied. Same output.
  const dateOnlyFormatter = new Intl.DateTimeFormat('en-CA', { timeZone });
  const sameDay = dateOnlyFormatter.format(start) === dateOnlyFormatter.format(end);
  if (sameDay) {
    const yearInTz = (date: Date) =>
      new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric' }).format(date);
    const isCurrentYear = yearInTz(start) === yearInTz(new Date());
    const dateStr = isCurrentYear
      ? new Intl.DateTimeFormat('en-US', {
          timeZone,
          month: 'short',
          day: 'numeric',
        }).format(start)
      : new Intl.DateTimeFormat('en-US', {
          timeZone,
          dateStyle: 'medium',
        }).format(start);
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeStyle: 'short',
    });
    let startTimeStr = timeFormatter.format(start);
    const endTimeStr = timeFormatter.format(end);
    const sameAmPm =
      (startTimeStr.endsWith(' AM') && endTimeStr.endsWith(' AM')) ||
      (startTimeStr.endsWith(' PM') && endTimeStr.endsWith(' PM'));
    if (sameAmPm) {
      startTimeStr = startTimeStr.replace(/\s*[AP]M$/, '');
    }
    return `${dateStr} • ${startTimeStr}-${endTimeStr}`;
  }

  return formatEventTimeRange(startIso, endIso, timeZone);
}

/** Build a stable group key for list rendering. */
export function eventGroupKey(event: GroupConsultEvent): string {
  return `${event.title}\n${event.description ?? ''}`;
}

/** Group sessions by title and description so the list view can show related time options together. */
export function groupEventsByTitleAndDescription(
  events: GroupConsultEvent[],
): GroupConsultEvent[][] {
  const grouped = new Map<string, GroupConsultEvent[]>();

  for (const event of sortEventsByStart(events)) {
    const key = eventGroupKey(event);
    const group = grouped.get(key);
    if (group) {
      group.push(event);
      continue;
    }
    grouped.set(key, [event]);
  }

  return [...grouped.values()];
}

/**
 * Convert a date-like value into a stable `YYYY-MM-DD` key for the given timezone, or `null` when
 * the value is not a real instant.
 *
 * @remarks TOTAL, never throwing — and the validity check is BEFORE `formatToParts`, which is the
 * only place it can do any work. `Intl.DateTimeFormat.prototype.formatToParts` throws
 * `RangeError: Invalid time value` on an Invalid Date rather than returning partial parts, so the
 * `if (!year || !month || !day) throw` this replaces was unreachable in both directions: for an
 * Invalid Date the formatter threw first, and for a valid one the requested options always yield
 * all three parts. A reader saw a defensive throw with a domain message and concluded malformed
 * dates were handled here, on the exact line where the outage happened.
 *
 * `en-CA` + `format` replaces `formatToParts` + three `find`s for the same reason the branch went:
 * with the guard gone the three part lookups are `string | undefined`, and giving them a default
 * would put `2026--02` back in the same spot as a second value nothing can produce. `en-CA`'s
 * pattern for this skeleton IS `y-MM-dd`, which is the key verbatim — the two `getGroupConsultDayKey`
 * cases in `group-consult-calendar.spec.ts` assert the exact string in two timezones, so an ICU
 * pattern change fails there rather than shipping a malformed key.
 *
 * The outage: this runs inside an unconditional `useMemo` in `use-group-consults.ts`, so it
 * executes during render, and a single webinar with a non-ISO `start` from
 * `GET /consult-scheduling/webinars` unmounted the entire SPA — header, footer, tab bar and the
 * 1:1 tab, which needs no webinar data at all. Returning `null` keeps a malformed session out of
 * the calendar instead. `use-group-consults.ts` also rejects such an element at the fetch
 * boundary, so both layers have to fail for a bad `start` to reach here.
 */
export function getGroupConsultDayKey(value: Date | string, timeZone: string): string | null {
  const date = typeof value === 'string' ? new Date(value) : value;
  // `instanceof` as well as the `NaN` check: callers pass `event.start`, which the type says is a
  // `string` but which comes off an API response, so a missing field arrives here as `undefined` on
  // a `GroupConsultEvent` whose type lied. `undefined.getTime()` would be a TypeError — the same
  // render-time throw in a different costume.
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Rehydrate a `YYYY-MM-DD` day key into a local Date for calendar rendering.
 *
 * @remarks The vendor destructured `dayKey.split('-').map(Number)` straight into
 * `new Date(year, month - 1, day)`. Under `noUncheckedIndexedAccess` those elements are
 * `number | undefined`, so the parts are read positionally instead — `Number(undefined)` is `NaN`,
 * which yields the same Invalid Date the vendor produced for a malformed key.
 */
export function dateFromGroupConsultDayKey(dayKey: string): Date {
  const parts = dayKey.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  return new Date(year, month - 1, day);
}

/** Build a stable `YYYY-MM` key from a local Date. */
export function getGroupConsultMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Format a selected calendar day heading for the day-detail panel. */
export function formatGroupConsultDayHeading(date: Date): string {
  const currentYear = new Date().getFullYear();
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(date.getFullYear() === currentYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** Bucket webinar sessions by local day in the selected display timezone. */
export function buildGroupConsultDayBuckets(
  events: GroupConsultEvent[],
  timeZone: string,
): GroupConsultDayBucket[] {
  const buckets = new Map<string, GroupConsultEvent[]>();

  for (const event of sortEventsByStart(events)) {
    const dayKey = getGroupConsultDayKey(event.start, timeZone);
    // A session whose `start` is not a real instant has no day to sit on, so it is dropped rather
    // than taking the whole render down with it. `use-group-consults.ts` rejects such an element at
    // the fetch boundary too — this is the second layer, and the one that covers a future caller
    // that skips the boundary.
    if (dayKey === null) continue;
    const dayEvents = buckets.get(dayKey);
    if (dayEvents) {
      dayEvents.push(event);
      continue;
    }
    buckets.set(dayKey, [event]);
  }

  return [...buckets.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([dayKey, dayEvents]) => {
      const titles = [...new Set(dayEvents.map((event) => event.title.trim()).filter(Boolean))];

      return {
        dayKey,
        date: dateFromGroupConsultDayKey(dayKey),
        events: dayEvents,
        titles,
      };
    });
}

/**
 * Pick the initial month and selected day so the month view opens on the most relevant available date.
 */
export function getDefaultGroupConsultMonthState(
  dayBuckets: GroupConsultDayBucket[],
  timeZone: string,
  now: Date = new Date(),
): {
  visibleMonth: Date;
  selectedDayKey: string | null;
} {
  // `now` defaults to `new Date()` at every production call site, so `null` here means a caller
  // passed an Invalid Date. That degrades to "no today" — first available session, else the real
  // current month — instead of throwing during render.
  const todayKey = getGroupConsultDayKey(now, timeZone);
  const todayMonthKey = todayKey?.slice(0, 7);
  const todayBucket =
    todayKey === null ? undefined : dayBuckets.find((bucket) => bucket.dayKey === todayKey);
  const currentMonthBuckets =
    todayMonthKey === undefined
      ? []
      : dayBuckets.filter((bucket) => bucket.dayKey.startsWith(todayMonthKey));
  const fallbackMonthDate =
    currentMonthBuckets[0]?.date ??
    dayBuckets[0]?.date ??
    (todayKey === null ? new Date() : dateFromGroupConsultDayKey(todayKey));
  const visibleMonth = startOfMonth(fallbackMonthDate);
  const selectedDayKey =
    todayBucket?.dayKey ??
    dayBuckets.find(
      (bucket) => getGroupConsultMonthKey(bucket.date) === getGroupConsultMonthKey(visibleMonth),
    )?.dayKey ??
    null;

  return { visibleMonth, selectedDayKey };
}

function sortEventsByStart(events: GroupConsultEvent[]): GroupConsultEvent[] {
  return [...events].sort(
    (left, right) => new Date(left.start).getTime() - new Date(right.start).getTime(),
  );
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
